import { AlertTriangle, Clock, Shield } from 'lucide-react';
import { KEVEntry, isDueDateApproaching, isDueDatePassed } from '../utils/cisaKev';

interface KEVBadgeProps {
  kevEntry: KEVEntry;
  variant?: 'full' | 'compact' | 'inline';
  className?: string;
}

/**
 * Reusable KEV badge component for displaying CISA KEV information
 *
 * Variants:
 * - full: Complete KEV information card
 * - compact: Badge with tooltip
 * - inline: Small inline badge for lists
 */
export default function KEVBadge({ kevEntry, variant = 'compact', className = '' }: KEVBadgeProps) {
  const duePassed = isDueDatePassed(kevEntry.dueDate);
  const dueApproaching = !duePassed && isDueDateApproaching(kevEntry.dueDate);

  if (variant === 'inline') {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white ${className}`}
        title="CISA Known Exploited Vulnerability"
      >
        <AlertTriangle className="w-3 h-3" />
        KEV
      </span>
    );
  }

  if (variant === 'compact') {
    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border-2 border-red-600 dark:border-red-500 ${className}`}
      >
        <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-500 flex-shrink-0" />
        <div className="flex flex-col">
          <span className="text-sm font-bold text-red-600 dark:text-red-500">
            ACTIVELY EXPLOITED
          </span>
          <span className="text-xs text-red-700 dark:text-red-400">
            Listed in CISA KEV Catalog
          </span>
        </div>
      </div>
    );
  }

  // Full variant
  return (
    <div className={`bg-red-50 dark:bg-red-950 border-2 border-red-600 dark:border-red-500 rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 space-y-3">
          <div>
            <h3 className="text-lg font-bold text-red-600 dark:text-red-500 mb-1">
              ACTIVELY EXPLOITED IN THE WILD
            </h3>
            <p className="text-sm text-red-700 dark:text-red-400">
              This vulnerability is listed in the CISA Known Exploited Vulnerabilities (KEV) catalog.
            </p>
          </div>

          <div className="space-y-2">
            <div>
              <span className="text-xs font-medium text-red-900 dark:text-red-300 uppercase">Vulnerability Name:</span>
              <p className="text-sm font-semibold text-red-800 dark:text-red-300 mt-0.5">
                {kevEntry.vulnerabilityName}
              </p>
            </div>

            <div>
              <span className="text-xs font-medium text-red-900 dark:text-red-300 uppercase">Vendor / Product:</span>
              <p className="text-sm text-red-800 dark:text-red-800 mt-0.5">
                {kevEntry.vendorProject} {kevEntry.product}
              </p>
            </div>

            <div>
              <span className="text-xs font-medium text-red-900 dark:text-red-300 uppercase">Required Action:</span>
              <p className="text-sm text-red-800 dark:text-red-300 mt-0.5 font-medium">
                {kevEntry.requiredAction}
              </p>
            </div>

            <div className="flex items-center gap-4 flex-wrap">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                duePassed
                  ? 'bg-red-600 text-white'
                  : dueApproaching
                  ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
              }`}>
                <Clock className="w-4 h-4" />
                <div className="text-xs">
                  <span className="font-medium">Due Date: </span>
                  <span className="font-bold">
                    {new Date(kevEntry.dueDate).toLocaleDateString()}
                  </span>
                  {duePassed && <span className="ml-1">(OVERDUE)</span>}
                  {dueApproaching && <span className="ml-1">(URGENT)</span>}
                </div>
              </div>

              {kevEntry.knownRansomwareCampaignUse === 'Known' && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                  <Shield className="w-4 h-4" />
                  <span className="text-xs font-medium">Ransomware Campaign Use</span>
                </div>
              )}
            </div>

            <div>
              <span className="text-xs font-medium text-red-900 dark:text-red-300 uppercase">Added to KEV:</span>
              <p className="text-sm text-red-800 dark:text-red-300 mt-0.5">
                {new Date(kevEntry.dateAdded).toLocaleDateString()}
              </p>
            </div>

            {kevEntry.notes && (
              <div>
                <span className="text-xs font-medium text-red-900 dark:text-red-300 uppercase">Notes:</span>
                <p className="text-sm text-red-800 dark:text-red-300 mt-0.5">
                  {kevEntry.notes}
                </p>
              </div>
            )}
          </div>

          <div className="pt-2 border-t border-red-200 dark:border-red-800">
            <a
              href="https://www.cisa.gov/known-exploited-vulnerabilities-catalog"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium underline"
            >
              View CISA KEV Catalog
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
